import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";

import { CreativeVersions } from "reducers/CreativeVersions";
import { loadCreative } from "reducers/CreativeDetails";

import EmptySection from "widgets/EmptySection";
import Breadcrumb from "./Breadcrumb";

import AutoOptimizationPanel from "./AutoOptimizationPanel";
import SelectVersions from "./SelectVersions";

import { useModalStack, noop } from "utils/common";
import {
	TARGET_METHODS,
	MAX_VERSIONS_BY_TARGET_METHOD
} from "utils/constants";

import './MultiVariantModal.css';


const TARGET_METHOD_VALUES = Object.values(TARGET_METHODS).filter(v => !/modern/i.test(v));

export default function MultiVariantModal({
	libraryId,
	creativeId,
	edit,
	onClose,
}){
	const dispatch = useDispatch();
	let modalTitle = "Multi Variant Dynamic Creative";

	const commonModalProps = {
		title: modalTitle,
		type: "modal",
		className: "multi-variant-modal white-title-bar",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	// eslint-disable-next-line no-unused-vars
	const [modalStack, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });
	const [targetMethod, setTargetMethod] = useState();

	const {
		isLoaded,
		creativeDecisioningName,
		isModernEnabled,
	} = useSelector((state) => {
		let loadedReqs = [state.libraryProperties?.isLoaded];
		if(edit){
			loadedReqs.push(state.creativeDetails?.isLoaded);
		}
		return {
			isLoaded: loadedReqs.every(v => v),
			creativeDecisioningName: state.creativeDetails?.decisioningName,
			isModernEnabled:state?.advertiserDetails?.data?.isMtaOptimizationEnabled,
		};
	});


	let isRotationSelected = (targetMethod === TARGET_METHODS.WEIGHTED_ROTATION)
		, isAutoOptimizationSelected = (targetMethod && !isRotationSelected)
		, [selectedObjective, setSelectedObjective] = useState()
		;

	let canContinue = useMemo(() => {
		if(/modern/i.test(targetMethod)){
			return !!selectedObjective;
		}

		/*`targetMethod` may be set to "modern", "legacy", or one of the may legacy specific values
			"legacy" indicates that the `AutoOptimizationPanel` is focused
			but the user cannot advance forward until they select a specific optimization value
		*/
		return TARGET_METHOD_VALUES.includes(targetMethod);
	}, [targetMethod, selectedObjective]);

	useEffect(() => {
		if(isLoaded && edit){
			setTargetMethod(creativeDecisioningName); /*
				This state update doesn't happen in time for `targetMethod` to be the correct value inside
				`openSelection`.

				We pass `openSelection` a `selectionMethod` argument, which corresponds to a `targetMethod` value,
				so we appropriately include weights, and dispatch the right creation action, but we have to set
				`targetMethod` so that the first step is populated correctly, if the user hits "Back" on the
				SelectVersions modal.
			*/
			openSelection(creativeDecisioningName);
		}
	}, [isLoaded, edit, creativeDecisioningName]);

	function closeModal(){
		onClose();
	}

	function openSelection(selectionMethod){
		let prompt = isRotationSelected ? "Select versions and weighting" : "Select versions to auto-optimize";
		function onSave({ success }){
			if(success){
				dispatch(CreativeVersions.loadCreativeVersions(libraryId, creativeId));
				dispatch(loadCreative(creativeId)); // We need this to update the DecisioningHeaader
				closeModal();
			}
		}
		function Configured(props){
			let modalProps = {
				...props,
				libraryId,
				creativeId,
				prompt,
				includeWeights: (selectionMethod === TARGET_METHODS.WEIGHTED_ROTATION),
				maxSelectableVersions: MAX_VERSIONS_BY_TARGET_METHOD[selectionMethod],

				onSelect: (selected) => {
					/*
						`selected` is an array with objects in the form:
							{
								id,
								weightMultiplier // Only present if `includeWeights` is true
							}
					*/
					if(isAutoOptimizationSelected){
						dispatch(CreativeVersions.createAutoOptimizedCreative(creativeId, selectionMethod, selected, selectedObjective))
							.then(onSave);
					} else {
						dispatch(CreativeVersions.createWeightedRotationCreative(libraryId, creativeId, selected))
							.then(onSave);
					}
				},
			};

			return <SelectVersions {...modalProps} />;
		}
		pushModal(Configured);
	}

	function Body() {
		let autoProps = {
			className:classNames("auto-optimization", {
				selected: isAutoOptimizationSelected,
				"can-continue":canContinue,
			}),
		};
		if(!isAutoOptimizationSelected){
			/**
			 * This onClick is consuming the onClick of the sub radio buttons when "Legacy" is selected
			 * There's probably a better way to do this
			 * 	- ideally using redux-form for all these fields instead of setState()
			 * But in the interest of time, only adding onClick when necessary works
			 */
			autoProps.onClick = () => {
				if(isModernEnabled){
					setTargetMethod(TARGET_METHODS.MODERN);
				} else {
					setTargetMethod(TARGET_METHODS.CLICK_THROUGH);
				}
			};
		}
		return (
			<div className="body-container">
				<ProgressIndicator key="loading" isLoading={!isLoaded} />
				<Breadcrumb currentStep={0} />
				<header className="header-info">
					<strong>Select how you would like to serve the versions for this creative</strong>
				</header>
				<div className="selection-routes">
					<div
						className={classNames("rotation", {
							selected: isRotationSelected,
							"can-continue":canContinue,
						})}
						onClick = {() => { setTargetMethod(TARGET_METHODS.WEIGHTED_ROTATION); }}
					>
						<EmptySection icon="rotation" header="Weighted Rotation" />
					</div>

					<div {...autoProps}>
						<AutoOptimizationPanel
							isSelected = {isAutoOptimizationSelected}
							isModernEnabled={isModernEnabled}
							currentValue = {targetMethod}
							onChange = {setTargetMethod}
							onObjective={setSelectedObjective}
							selectedObjective={selectedObjective}
						/>
					</div>
				</div>
			</div>
		);
	}

	function Footer() {
		return (
			<>
				<Button
					theme="default"
					type="reset"
					onClick = { closeModal }
				>
					Cancel
				</Button>

				<Button
					onClick = { () => { openSelection(targetMethod); } }
					disabled = { !canContinue }
				>
					Next to Select Versions
				</Button>
			</>
		);
	}

	if(ModalStackHead){
		return <ModalStackHead />;
	}

	return (
		<Modal
			{...commonModalProps}
			renderContent = { Body }
			renderFooter = { Footer }
		/>
	);
}

MultiVariantModal.displayName = "MultiVariantModal";
MultiVariantModal.propTypes = {
	libraryId: PropTypes.string.isRequired,
	creativeId: PropTypes.string.isRequired,
	onClose: PropTypes.func,
	edit: PropTypes.bool,
};
MultiVariantModal.defaultProps = {
	onClose: noop,
	edit: false,
};
